import { useCallback, useContext, useMemo, useState } from 'react'
import { FiX } from 'react-icons/fi'

import { Modal } from '~/components/Modal'
import { CardForm } from '~/forms/Sales/Checkout/CardForm'
import { CardDataFormValues } from '~/forms/Sales/Checkout/CardForm/schema'
import {
  computeDiscountValue,
  createSubscriptionUpgrade,
  numberToBRL,
} from '~/modules/sales/services'
import { Discount } from '~/modules/sales/types'
import { insertToast } from '~/modules/toasts/services'

import { subscriptionsContext } from '../../..'
import { planOfferContext } from '../UpgradeOffer'
import { DiscountInput } from './DiscountInput'

type Props = {
  visible: boolean
  onClose(): void
}

const _getDistanceInDays = (start: Date, end: Date) => {
  const s = start.getTime()
  const e = end.getTime()
  return Math.ceil((e - s) / (1000 * 60 * 60 * 24))
}

export const UpgradeModal: React.FC<Props> = ({ visible, onClose }) => {
  const { plan } = useContext(planOfferContext)
  const { subscription, updateSubscription } = useContext(subscriptionsContext)
  const [discount, setDiscount] = useState<Discount>()
  const [payload, setPayload] = useState<any>({
    planId: plan.id,
  })

  const handleGetDiscount = useCallback((discount?: Discount) => {
    setDiscount(discount)
  }, [])

  const handleGetCoupon = useCallback((coupon?: string) => {
    setPayload((prev: any) => ({ ...prev, coupon }))
  }, [])

  const handleSubmit = useCallback(
    async (card: CardDataFormValues) => {
      await createSubscriptionUpgrade({ ...payload, card })
        .then((data) => {
          insertToast({
            duration: 3,
            title: 'Sucesso!',
            message: 'Assinatura atualizada com sucesso.',
          })
          updateSubscription(data)
          onClose()
        })
        .catch((e: Error) => {
          insertToast({
            duration: 4,
            title: 'Ops',
            message: e.message,
            type: 'error',
          })
        })
    },
    [onClose, updateSubscription, payload],
  )

  const upgradeDiscount = useMemo(() => {
    const today = new Date()
    const subscriptionPeriodStart = new Date(subscription!.termStart)
    const subscriptionPeriodEnd = new Date(subscription!.termEnd)

    const daysUsed = _getDistanceInDays(subscriptionPeriodStart, today)
    const daysInPeriod =
      _getDistanceInDays(subscriptionPeriodStart, subscriptionPeriodEnd) - 1

    const currentPlanTotalAmount = subscription!.plan?.value ?? 0

    const currentPlanValuePerDay = currentPlanTotalAmount / daysInPeriod
    const valuePerDaysUsed = currentPlanValuePerDay * daysUsed
    const currentPlanRemainder = currentPlanTotalAmount - valuePerDaysUsed

    return {
      daysUsed,
      daysInPeriod,
      currentPlanTotalAmount: Math.ceil(currentPlanTotalAmount),
      currentPlanValuePerDay: Math.ceil(currentPlanValuePerDay),
      valuePerDaysUsed: Math.ceil(valuePerDaysUsed),
      currentPlanRemainder: Math.ceil(currentPlanRemainder),
    }
  }, [subscription])

  const totalValue = useMemo(
    () =>
      (discount
        ? plan.value -
          computeDiscountValue(discount.type, discount.value, plan.value)
        : plan.value - upgradeDiscount.currentPlanRemainder) / 100,

    [discount, plan.value, upgradeDiscount.currentPlanRemainder],
  )

  return (
    <Modal visible={visible}>
      <div className='flex items-center justify-between mb-2'>
        <h2 className='text-xl font-medium truncate'>
          Plano {plan.name} &bull; {plan.licensesAmount}
        </h2>
        <button
          className='bg-white hover:bg-gpa-gray-50 p-1.5 rounded-full'
          onClick={onClose}
        >
          <FiX size={24} />
        </button>
      </div>

      <div
        className='flex flex-col gap-4 bg-gpa-blue-50 p-2 py-6 my-6
          rounded-lg'
      >
        <div className='flex justify-between px-3 pb-1'>
          <p>Valor do plano</p>
          <p>{numberToBRL(plan.value / 100)}</p>
        </div>

        {discount ? (
          <div className='flex justify-between px-3 pb-1'>
            <p>Desconto</p>
            <p>
              {numberToBRL(
                computeDiscountValue(
                  discount.type,
                  discount.value,
                  plan.value,
                ) / 100,
              )}
            </p>
          </div>
        ) : null}

        <div className='flex justify-between px-3 pb-1'>
          <p>Valor do saldo</p>
          <p>{numberToBRL(upgradeDiscount.currentPlanRemainder / 100)}</p>
        </div>

        <div
          className='flex justify-between px-3 pb-1 pt-3 border-t-2
          border-gpa-blue-500'
        >
          <strong>Valor total da nova assinatura</strong>
          <p>{totalValue > 0 ? numberToBRL(totalValue) : numberToBRL(0)}</p>
        </div>

        <DiscountInput
          planId={plan.id}
          onGetDiscount={handleGetDiscount}
          onGetCoupon={handleGetCoupon}
        />
      </div>

      <CardForm onSubmit={handleSubmit} paymentPlan={plan} />
    </Modal>
  )
}
